import type { ComponentType } from "react";
import { type Params, useLocation, useMatches } from "react-router-dom";

import { isRecord } from "@/apps/admin/utilities/typeGuard";

/** What a crumb that names its entity is drawn with: the route's params, and its title to fall back on. */
export type CrumbLabelProps = { params: Params<string>; title: string };
export type CrumbLabel = ComponentType<CrumbLabelProps>;

/**
 * `href` is null for a section with no page of its own (a handle with `hasPage: false`).
 * `Label` draws a crumb that names its entity ("Lithium mining" rather than "Collector"); null
 * draws `label`, the route's title.
 */
export type RouteCrumb = {
  label: string;
  href: string | null;
  Label: CrumbLabel | null;
  params: Params<string>;
};

/**
 * A crumb for depth that lives in the query, not in a route (FB-48). A route handle gives
 * `searchTrail`, a function of the address's search; each crumb links to `search` on that route.
 */
export type SearchCrumb = { label: string; search: string };

const isSearchTrail = (
  value: unknown
): value is (search: URLSearchParams) => SearchCrumb[] =>
  typeof value === "function";

// The handles that name their entity, each with the component that draws its name. Filled once,
// as the route table is built (`withCrumbLabel`), and only read after.
const crumbLabels = new WeakMap<object, CrumbLabel>();

/**
 * A route handle whose crumb names its entity: `Label` loads the name from the route's params
 * and draws it, falling back to the route's title until it has it.
 */
export const withCrumbLabel = <THandle extends object>(
  handle: THandle,
  Label: CrumbLabel
): THandle => {
  const named = { ...handle };
  crumbLabels.set(named, Label);
  return named;
};

/** A route handle's `defaultQueryParams` as a search string, so a crumb opens the page as its links do. */
const searchFromDefaultQueryParams = (params: unknown): string => {
  if (!isRecord(params)) {
    return "";
  }
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== "") {
      searchParams.set(key, String(value));
    }
  }
  const search = searchParams.toString();
  return search ? `?${search}` : "";
};

/**
 * The trail of the matched routes that have a title, root excluded: the top bar draws it, and a
 * page header takes the last one as its default title. An index route matches a second time
 * with a trailing slash, and is dropped unless its handle says `crumbOfIndex`.
 */
export const useRouteTrail = (): RouteCrumb[] => {
  const { search } = useLocation();
  return useRouteMatchTrail(new URLSearchParams(search));
};

const useRouteMatchTrail = (search: URLSearchParams): RouteCrumb[] =>
  useMatches()
    .slice(1)
    .filter(
      ({ pathname, handle }) =>
        !/^\/.+\/$/.test(pathname) ||
        (isRecord(handle) && handle.crumbOfIndex === true)
    )
    .flatMap(({ pathname: matched, handle, params }) => {
      // An index route that names itself (the Directory under Providers) is the page its parent
      // path opens: its crumb links there, without the trailing slash.
      const pathname = matched.replace(/(.)\/$/, "$1");
      if (!isRecord(handle) || typeof handle.title !== "string") {
        return [];
      }
      const deeper = isSearchTrail(handle.searchTrail)
        ? handle.searchTrail(search)
        : [];
      return [
        {
          label: handle.title,
          href:
            handle.hasPage === false
              ? null
              : // With depth under it, this crumb opens the page's own address, not its default one.
                `${pathname}${deeper.length > 0 ? "" : searchFromDefaultQueryParams(handle.defaultQueryParams)}`,
          Label: crumbLabels.get(handle) ?? null,
          params,
        },
        ...deeper.map(({ label, search: crumbSearch }) => ({
          label,
          href: `${pathname}?${crumbSearch}`,
          Label: null,
          params,
        })),
      ];
    });
